import { TouchEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  BodyLgBold,
  BodyMedReg,
  BodySmBold,
  BodyXlBold,
  BodyXsBold,
  DisplaySmReg,
  MonoSmReg,
  TitleSmReg,
} from '@seed-health/tokens';
import { mqLargeMin } from 'styled/tools/media-queries';
import Icon from 'components/global/Icon/Primary';
import CalloutArrow from './callout-arrow';
import ChartAnimation, { CHART_SCALE } from './chart-animation';
import { MicrobiomeModuleContent } from './data';

// Desktop chart size relative to the mobile design, single vs side-by-side
const DESKTOP_SCALE = 1.412;
const DESKTOP_PAIR_SCALE = 1.21;
// Transparent band above and below the plot in each 250px-tall Lottie canvas
const CANVAS_BAND = 15.85;
// A swipe must travel this far (px) and be mostly horizontal to change slides
const SWIPE_DISTANCE = 44;
const SWIPE_RATIO = 1.3;

const Module = styled.section`
  min-width: 0;
  margin: 0 var(--spacing-16);
  border-radius: var(--radius-xl);
  padding: var(--spacing-32) var(--spacing-16) var(--spacing-16);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-24);
  background-color: var(--fill-brand);

  sup {
    font-size: 0.645em;
    color: inherit;
  }

  @media ${mqLargeMin} {
    margin: 0;
    grid-column: 1 / -1;
    padding: var(--spacing-16);
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: 1fr auto auto auto 1fr;
    grid-template-areas:
      '. chart'
      'title chart'
      'subtitle chart'
      'callout chart'
      '. chart';
    column-gap: var(--spacing-16);
    row-gap: 0;
  }
`;

const leftColumn = css`
  @media ${mqLargeMin} {
    margin-left: var(--spacing-48);
    margin-right: var(--spacing-24);
  }
`;

const Title = styled.h2`
  ${TitleSmReg};
  margin: 0;
  color: var(--text-inverse-primary);
  ${leftColumn};

  @media ${mqLargeMin} {
    ${DisplaySmReg};
    grid-area: title;
    min-height: 200px;
    margin-bottom: var(--spacing-48);
    display: flex;
    align-items: center;
  }
`;

const Subtitle = styled.p`
  ${BodyMedReg};
  margin: 0;
  color: var(--text-inverse-primary);
  ${leftColumn};

  @media ${mqLargeMin} {
    grid-area: subtitle;
    margin-bottom: var(--spacing-16);
  }
`;

const Carousel = styled.div`
  position: relative;
  min-width: 0;

  @media ${mqLargeMin} {
    display: contents;
  }
`;

const Track = styled.div`
  display: grid;
  touch-action: pan-y pinch-zoom;

  @media ${mqLargeMin} {
    display: contents;
  }
`;

// Inactive slides stay mounted; only desktop keeps them in layout
const SlideUnit = styled.div<{ $active: boolean }>`
  grid-area: 1 / 1;
  min-width: 0;
  border: var(--stroke-default) solid var(--outline-light-20);
  border-radius: var(--radius-md);
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: var(--spacing-8);
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};

  @media ${mqLargeMin} {
    display: contents;
  }
`;

const Callout = styled.div`
  padding: var(--spacing-16);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-16);
  ${leftColumn};

  @media ${mqLargeMin} {
    grid-area: callout;
    border: var(--stroke-default) solid var(--outline-light-20);
    border-radius: var(--radius-md);
  }
`;

const CalloutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
`;

const CalloutTitle = styled.h3`
  ${BodyLgBold};
  margin: 0;
  color: var(--text-inverse-primary);

  @media ${mqLargeMin} {
    ${BodyXlBold};
  }
`;

const CalloutDescription = styled.p`
  ${BodyMedReg};
  margin: 0;
  color: var(--text-inverse-primary);
`;

const ChartCard = styled.div<{ $hasDots: boolean }>`
  flex: 1;
  border-radius: var(--radius-md);
  padding: var(--spacing-24) var(--spacing-16)
    ${({ $hasDots }) => ($hasDots ? 'var(--spacing-48)' : 'var(--spacing-16)')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
  background-color: var(--fill-tertiary);

  @media ${mqLargeMin} {
    grid-area: chart;
    min-height: 518px;
    padding: var(--spacing-16) var(--spacing-24)
      ${({ $hasDots }) =>
        $hasDots ? 'var(--spacing-40)' : 'var(--spacing-16)'};
    justify-content: center;
    gap: var(--spacing-32);
  }
`;

const Toggle = styled.div`
  border-radius: var(--radius-full);
  padding: var(--spacing-4);
  display: flex;
  background-color: var(--color-primary-seed-green-10);

  @media ${mqLargeMin} {
    display: none;
  }
`;

// Shared hover, pressed and keyboard-focus states for the module's buttons
const interactionStates = css`
  &:hover {
    opacity: var(--hover);
  }

  &:active {
    opacity: var(--active);
  }

  &:focus-visible {
    opacity: var(--focused);
    outline: 3px solid var(--focus-color, currentColor);
    outline-offset: 2px;
  }
`;

const ToggleButton = styled.button<{ $selected: boolean }>`
  ${BodyXsBold};
  position: relative;
  border: 0;
  border-radius: var(--radius-full);
  padding: var(--spacing-4) var(--spacing-8);
  color: var(--text-primary);
  -webkit-tap-highlight-color: transparent;
  background-color: ${({ $selected }) =>
    $selected ? 'var(--fill-tertiary)' : 'transparent'};
  cursor: pointer;

  /* Extends the hit area to 44px tall without changing the pill */
  &::before {
    content: '';
    position: absolute;
    inset: -8px 0;
  }

  ${interactionStates};
`;

const Charts = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  @media ${mqLargeMin} {
    justify-content: space-around;
  }
`;

const ChartSlot = styled.div<{ $mobileHidden: boolean; $single: boolean }>`
  display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'block')};
  min-width: 0;

  @media ${mqLargeMin} {
    display: block;

    /* Figma's single-chart box ends at the plot, so trim the canvas's empty band */
    margin: ${({ $single }) =>
      $single ? `${-CANVAS_BAND * DESKTOP_SCALE * CHART_SCALE}px 0` : '0'};
  }
`;

const Figcaption = styled.figcaption`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);

  @media ${mqLargeMin} {
    padding: 0 var(--spacing-32);
  }
`;

const FigureLabel = styled.span`
  ${MonoSmReg};
  color: var(--text-primary);
`;

const Caption = styled.span`
  ${BodyXsBold};
  color: var(--text-primary);

  @media ${mqLargeMin} {
    ${BodySmBold};
  }
`;

const Figure = styled.figure`
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);

  @media ${mqLargeMin} {
    gap: var(--spacing-32);
  }
`;

const Dots = styled.div`
  position: absolute;
  right: var(--spacing-16);
  bottom: var(--spacing-16);
  padding: var(--spacing-8);
  display: flex;
  align-items: center;
  gap: var(--spacing-8);

  @media ${mqLargeMin} {
    position: static;
    grid-area: chart;
    align-self: end;
    justify-self: end;
    margin: 0 var(--spacing-24) var(--spacing-16) 0;
  }
`;

const Dot = styled.button<{ $active: boolean }>`
  position: relative;
  border: 0;
  border-radius: var(--radius-full);
  padding: 0;
  width: ${({ $active }) =>
    $active ? 'var(--spacing-16)' : 'var(--spacing-8)'};
  height: var(--spacing-8);
  background-color: ${({ $active }) =>
    $active ? 'var(--fill-brand)' : 'var(--scrim-dark-20)'};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: width 0.3s ease;

  /* 8px dots sit 8px apart, so the hit area grows vertically only */
  &::before {
    content: '';
    position: absolute;
    inset: -18px -4px;
  }

  ${interactionStates};

  @media ${mqLargeMin} {
    background-color: ${({ $active }) =>
      $active ? 'var(--fill-brand)' : 'var(--scrim-glass-dark-35)'};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Desktop only; sits 24px inside the chart card's edge, centered on it
const Arrow = styled.button<{ $side: 'previous' | 'next' }>`
  display: none;

  @media ${mqLargeMin} {
    position: relative;
    grid-area: chart;
    align-self: center;
    justify-self: ${({ $side }) => ($side === 'previous' ? 'start' : 'end')};
    margin: 0 var(--spacing-24);
    border: 0;
    padding: 0;
    display: flex;
    color: var(--actions-primary);
    background: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    /* Grows the 16px icon to a 44px hit area */
    &::before {
      content: '';
      position: absolute;
      inset: -14px;
    }

    ${interactionStates};
  }
`;

interface CobioticsMicrobiomeModuleProps {
  content: MicrobiomeModuleContent;
}

const CobioticsMicrobiomeModule = ({
  content,
}: CobioticsMicrobiomeModuleProps) => {
  const { productName, title, subtitle, slides } = content;
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeChart, setActiveChart] = useState(0);
  const hasMultipleSlides = slides.length > 1;

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const previousArrow = useRef<HTMLButtonElement>(null);
  const nextArrow = useRef<HTMLButtonElement>(null);
  const arrowToFocus = useRef<'previous' | 'next' | null>(null);
  const lastSlide = slides.length - 1;

  // The pressed arrow disappears at either end, so focus moves to the other one
  useEffect(() => {
    const target =
      arrowToFocus.current === 'previous'
        ? previousArrow.current
        : arrowToFocus.current === 'next'
          ? nextArrow.current
          : null;
    arrowToFocus.current = null;
    target?.focus();
  }, [activeSlide]);

  const stepSlide = (step: 1 | -1) => {
    const next = activeSlide + step;
    if (next === 0) arrowToFocus.current = 'next';
    if (next === lastSlide) arrowToFocus.current = 'previous';
    setActiveSlide(next);
  };

  const handleTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const farEnough = Math.abs(dx) >= SWIPE_DISTANCE;
    const mostlyHorizontal = Math.abs(dx) >= Math.abs(dy) * SWIPE_RATIO;
    if (!farEnough || !mostlyHorizontal) return;
    const next = activeSlide + (dx < 0 ? 1 : -1);
    setActiveSlide(Math.min(Math.max(next, 0), lastSlide));
  };

  return (
    <Module>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
      <Carousel>
        <Track
          onTouchStart={hasMultipleSlides ? handleTouchStart : undefined}
          onTouchEnd={hasMultipleSlides ? handleTouchEnd : undefined}
        >
          {slides.map((slide, index) => {
            const isActive = index === activeSlide;
            const isPair = slide.charts.length > 1;
            return (
              <SlideUnit key={slide.figure} $active={isActive}>
                <Callout>
                  <CalloutArrow direction={slide.direction} />
                  <CalloutContent>
                    <CalloutTitle>{slide.title}</CalloutTitle>
                    <CalloutDescription>{slide.description}</CalloutDescription>
                  </CalloutContent>
                </Callout>
                <ChartCard $hasDots={hasMultipleSlides}>
                  {isPair && (
                    <Toggle role='group' aria-label='Choose a bacterial group'>
                      {slide.charts.map((chart, chartIndex) => (
                        <ToggleButton
                          key={chart.name}
                          type='button'
                          $selected={chartIndex === activeChart}
                          aria-pressed={chartIndex === activeChart}
                          onClick={() => setActiveChart(chartIndex)}
                        >
                          {chart.name}
                        </ToggleButton>
                      ))}
                    </Toggle>
                  )}
                  <Figure>
                    <Charts>
                      {slide.charts.map((chart, chartIndex) => (
                        <ChartSlot
                          key={chart.name}
                          $mobileHidden={isPair && chartIndex !== activeChart}
                          $single={!isPair}
                        >
                          <ChartAnimation
                            chart={chart}
                            productName={productName}
                            active={isActive}
                            replayKey={`${activeSlide}-${activeChart}`}
                            desktopScale={
                              isPair ? DESKTOP_PAIR_SCALE : DESKTOP_SCALE
                            }
                          />
                        </ChartSlot>
                      ))}
                    </Charts>
                    <Figcaption>
                      <FigureLabel>{slide.figure}</FigureLabel>
                      <Caption>{slide.caption}</Caption>
                    </Figcaption>
                  </Figure>
                </ChartCard>
              </SlideUnit>
            );
          })}
        </Track>
        {activeSlide > 0 && (
          <Arrow
            ref={previousArrow}
            type='button'
            $side='previous'
            aria-label='Previous result'
            onClick={() => stepSlide(-1)}
          >
            <Icon name='chevron-left' size='md' />
          </Arrow>
        )}
        {activeSlide < lastSlide && (
          <Arrow
            ref={nextArrow}
            type='button'
            $side='next'
            aria-label='Next result'
            onClick={() => stepSlide(1)}
          >
            <Icon name='chevron-right' size='md' />
          </Arrow>
        )}
        {hasMultipleSlides && (
          <Dots role='group' aria-label='Study results'>
            {slides.map((slide, index) => (
              <Dot
                key={slide.figure}
                type='button'
                $active={index === activeSlide}
                aria-label={`Show result ${index + 1} of ${slides.length}`}
                aria-current={index === activeSlide ? 'true' : undefined}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </Dots>
        )}
      </Carousel>
    </Module>
  );
};

export default CobioticsMicrobiomeModule;
